import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import ConfirmModel from "../../components/ConfirmModel";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import { updateUserData } from "../../utils/CommonFunction";
import { getApiData, postFormData } from "../../utils/APIHelper";
import {
    getDesignBidDetails,
    submitDesignBid,
    updateDesignBid,
} from "../../services/designBidService";
import DesignProposalResponse from "./components/DesignProposalResponse";
import DesignDeliverables, {
    allowsRevisions,
} from "./components/DesignDeliverables";
import DesignPayments from "./components/DesignPayments";
import DesignReview from "./components/DesignReview";
import ProjectDrawer from "./components/ProjectDrawer";

const TABS = [
    { id: "proposal", label: "Proposal Response" },
    { id: "deliverables", label: "Deliverables" },
    { id: "payments", label: "Payments" },
    { id: "review", label: "Review" },
];

// Numbered pill tab — same look as the admin portal's design scope
// stepper (compact rounded-full pills with a number bubble).
function PillTab({ index, label, selected, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "0 14px",
                height: "36px",
                borderRadius: "999px",
                border: "none",
                fontSize: "13px",
                fontWeight: 500,
                whiteSpace: "nowrap",
                fontFamily: "Inter Tight",
                cursor: "pointer",
                transition: "all 0.2s ease",
                backgroundColor: selected ? "#0d0d0d" : "#F7F7F7",
                color: selected ? "#fff" : "#949daa",
            }}
        >
            <span
                style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    flexShrink: 0,
                    backgroundColor: selected ? "#3d3d3d" : "#fff",
                    color: selected ? "#fff" : "#949daa",
                }}
            >
                {`0${index + 1}`}
            </span>
            <span>{label}</span>
        </button>
    );
}

export default function RFQDesignPricing() {
    // Route mirrors /rfq/pricing/:id/:bidId — `bidId` is the bid project id
    // the design-bid endpoints key off.
    const { bidId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { showSecurityModal, handleSecurityModalClose } =
        useTokenValidation();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    const [proposalImages, setProposalImages] = useState([]);
    const [proposalDescription, setProposalDescription] = useState("");
    const [proposalAmount, setProposalAmount] = useState("");
    const [deliverables, setDeliverables] = useState([]);
    // Read-only payment split defined on the design scope —
    // [{name, percentage, type, category, group_id?}].
    const [paymentRows, setPaymentRows] = useState([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState(true);

    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const project = data?.project;
    const isDisabled =
        !!data &&
        data?.proposal?.status !== "awaiting-contractor-confirmation";

    const fetchData = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const response = await getDesignBidDetails(bidId);
            const bid = response?.data;
            if (!bid) {
                setData(null);
                return;
            }
            setData(bid);

            // Project image — same primary-image logic as the execution
            // flow (the tag-less entry is the proposal image).
            const images = [];
            if (bid.project?.project_image) {
                const raw = Array.isArray(bid.project.project_image)
                    ? bid.project.project_image
                    : [bid.project.project_image];
                raw.forEach((img) =>
                    images.push({ ...img, image: img.image || img.path }),
                );
            }
            setProposalImages(images);

            // The saved description comes back on project.description
            // (scope_of_work stays null for design bids).
            setProposalDescription(
                bid.project?.description || bid.proposal?.scope_of_work || "",
            );
            // The contractor's design proposal amount lives on
            // proposal.project_cost.
            const amount = bid.proposal?.project_cost;
            setProposalAmount(amount ? String(amount) : "");

            // Weeks per deliverable live on proposal.timeline, keyed by
            // the deliverable name.
            const timeline = bid.proposal?.timeline || {};
            setDeliverables(
                (bid.deliverables || []).map((d) => ({
                    id: d.id,
                    name: d.name || "",
                    description: d.description || "",
                    weeks:
                        timeline[d.name] != null
                            ? String(timeline[d.name])
                            : "",
                    revisions:
                        d.metadata?.revisions != null
                            ? String(d.metadata.revisions)
                            : "",
                    sub_deliverables: d.metadata?.sub_deliverables || [],
                    metadata: d.metadata || {},
                })),
            );

            setPaymentRows(
                (bid.payments || []).map((p) => ({
                    group_id: p.group_id,
                    name: p.group_name || p.name || "-",
                    percentage: p.percentage,
                    // Backend-computed amount — stays null until the
                    // payments have been synced onto the proposal.
                    amount: p.amount,
                    type: p.type,
                    category: p.category,
                    description: p.description,
                })),
            );
        } catch (error) {
            console.error("Error fetching design bid details:", error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        updateUserData();
    }, []);

    useEffect(() => {
        if (bidId) fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bidId]);

    // ---- Image upload (same logic as the execution bid details page) ----
    const handleProposalImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        const formData = new FormData();
        files.forEach((file, index) => {
            formData.append(`file_${index + 1}`, file);
        });
        formData.append("type", "project");
        formData.append("project_id", bidId);

        try {
            const response = await postFormData("api/project/files", formData);
            if (response.success && response.images) {
                setProposalImages((prev) => [...prev, ...response.images]);
            }
        } catch (error) {
            console.error("Error uploading proposal images:", error);
        }
    };

    const handleDeleteProposalImage = async (imageId) => {
        try {
            const response = await getApiData(
                `api/project/delete-files/${imageId}`,
                "POST",
            );
            if (response.success) {
                setProposalImages((prev) =>
                    prev.filter((img) => img.id !== imageId),
                );
            }
        } catch (error) {
            console.error("Error deleting proposal image:", error);
        }
    };

    // ---- Payload builders ----
    const buildMilestones = () =>
        deliverables
            .filter((d) => (d.name || "").trim())
            .map((d) => {
                const metadata = {
                    ...(d.metadata || {}),
                    sub_deliverables: (d.sub_deliverables || []).filter((s) =>
                        (s || "").trim(),
                    ),
                };
                // Revisions only apply to concept/render/material/drawing.
                if (
                    allowsRevisions(d.name) &&
                    d.revisions !== "" &&
                    d.revisions != null
                ) {
                    metadata.revisions = Number(d.revisions);
                } else {
                    delete metadata.revisions;
                }
                return {
                    ...(d.id ? { id: d.id } : {}),
                    name: d.name.trim(),
                    description: d.description || "",
                    tags: "DESIGN",
                    metadata,
                };
            });

    // Timeline (weeks per deliverable) — entered directly on the
    // deliverable, sent as proposal_data.timeline.
    const buildTimeline = () => {
        const timeline = {};
        deliverables.forEach((d) => {
            const name = (d.name || "").trim();
            const weeks = Number(d.weeks);
            if (name && d.weeks !== "" && d.weeks != null && weeks > 0) {
                timeline[name] = weeks;
            }
        });
        return timeline;
    };

    const buildPayments = () => {
        const totalAmount = Number(proposalAmount) || 0;
        return paymentRows.map((row) => ({
            ...(row.group_id ? { group_id: row.group_id } : {}),
            name: row.name,
            percentage: Number(row.percentage) || 0,
            amount: (totalAmount * (Number(row.percentage) || 0)) / 100,
            type: row.type,
            category: row.category,
        }));
    };

    // ---- Per-tab save handlers ----
    const saveProposal = async () => {
        if (
            proposalAmount !== "" &&
            (isNaN(Number(proposalAmount)) || Number(proposalAmount) < 0)
        ) {
            toast.error("Please enter a valid proposal amount");
            return false;
        }
        setIsSaving(true);
        try {
            const proposalData = {};
            if (proposalAmount !== "") {
                proposalData.project_cost = Number(proposalAmount);
            }
            if (proposalDescription && proposalDescription.trim() !== "") {
                proposalData.description = proposalDescription;
            }
            await updateDesignBid(bidId, { proposal_data: proposalData });
            toast.success("Proposal saved successfully!");
            return true;
        } catch (error) {
            console.error("Error saving proposal:", error);
            toast.error("Error saving proposal. Please try again.");
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const saveDeliverables = async () => {
        const milestones = buildMilestones();
        if (milestones.length === 0) {
            toast.error("Add at least one deliverable");
            return false;
        }
        // Every deliverable must have a positive week count.
        const missingWeeks = deliverables.filter(
            (d) => (d.name || "").trim() && (!d.weeks || Number(d.weeks) <= 0),
        );
        if (missingWeeks.length > 0) {
            toast.error(
                `Please enter weeks for: ${missingWeeks
                    .map((d) => d.name)
                    .join(", ")}`,
            );
            return false;
        }
        // Revisions are mandatory for concept/render/material/drawing.
        const missingRevisions = deliverables.filter(
            (d) =>
                (d.name || "").trim() &&
                allowsRevisions(d.name) &&
                (!d.revisions || Number(d.revisions) <= 0),
        );
        if (missingRevisions.length > 0) {
            toast.error(
                `Please enter the number of revisions for: ${missingRevisions
                    .map((d) => d.name)
                    .join(", ")}`,
            );
            return false;
        }
        // Weeks per deliverable are sent as the proposal timeline
        // alongside the milestones (payload shape unchanged).
        const timeline = buildTimeline();
        setIsSaving(true);
        try {
            await updateDesignBid(bidId, {
                milestones,
                ...(Object.keys(timeline).length > 0
                    ? { proposal_data: { timeline } }
                    : {}),
            });
            toast.success("Deliverables saved successfully!");
            // Refresh silently so newly added deliverables pick up ids.
            await fetchData({ silent: true });
            return true;
        } catch (error) {
            console.error("Error saving deliverables:", error);
            toast.error("Error saving deliverables. Please try again.");
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const savePayments = async () => {
        // Payments are read-only (defined on the design scope) — just sync
        // them onto the proposal so payment groups exist for submission.
        if (paymentRows.length === 0) {
            toast.error("No payment structure defined for this design scope");
            return false;
        }
        setIsSaving(true);
        try {
            await updateDesignBid(bidId, { payments: buildPayments() });
            // Refresh so the backend-computed payment amounts land in
            // state — the submit check relies on them.
            await fetchData({ silent: true });
            return true;
        } catch (error) {
            console.error("Error saving payments:", error);
            toast.error("Error saving payments. Please try again.");
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleNext = async () => {
        if (isDisabled) {
            setActiveTab((prev) => Math.min(prev + 1, TABS.length - 1));
            return;
        }
        if (activeTab === 0) {
            const saved = await saveProposal();
            if (saved) setActiveTab(1);
        } else if (activeTab === 1) {
            const saved = await saveDeliverables();
            if (saved) setActiveTab(2);
        } else if (activeTab === 2) {
            const saved = await savePayments();
            if (saved) setActiveTab(3);
        }
    };

    // ---- Submit bid ----
    // Click-time checks — instead of a disabled button, each failing
    // check navigates to the tab where it can be fixed, with a toast.
    const handleSubmitClick = () => {
        if (!proposalAmount || Number(proposalAmount) <= 0) {
            toast.error(
                "Please enter & save a proposal amount before submitting",
            );
            setActiveTab(0);
            return;
        }

        // Description check intentionally not mandatory for design bids:
        // if ((proposalDescription || "").trim() === "") {
        //     toast.error("Please add a description before submitting");
        //     setActiveTab(0);
        //     return;
        // }

        const namedDeliverables = deliverables.filter((d) =>
            (d.name || "").trim(),
        );
        if (namedDeliverables.length === 0) {
            toast.error("Please add deliverables before submitting");
            setActiveTab(1);
            return;
        }
        // Weeks are entered on the Deliverables tab (index 1).
        const missingWeeks = namedDeliverables.filter(
            (d) => !d.weeks || Number(d.weeks) <= 0,
        );
        if (missingWeeks.length > 0) {
            toast.error(
                `Please enter & save the number of weeks for: ${missingWeeks
                    .map((d) => d.name)
                    .join(", ")}`,
            );
            setActiveTab(1);
            return;
        }
        // Revisions are mandatory for concept/render/material/drawing.
        const missingRevisions = namedDeliverables.filter(
            (d) =>
                allowsRevisions(d.name) &&
                (!d.revisions || Number(d.revisions) <= 0),
        );
        if (missingRevisions.length > 0) {
            toast.error(
                `Please enter & save the number of revisions for: ${missingRevisions
                    .map((d) => d.name)
                    .join(", ")}`,
            );
            setActiveTab(1);
            return;
        }

        // Each payment group must carry a backend-computed amount — that
        // only happens once the Payments tab has been saved via Next.
        if (
            paymentRows.length === 0 ||
            paymentRows.some((p) => p.amount == null)
        ) {
            toast.error("Please save the Payments tab before submitting");
            setActiveTab(2);
            return;
        }

        setShowSubmitModal(true);
    };

    const handleSubmitBid = async () => {
        setIsSubmitting(true);
        try {
            const response = await submitDesignBid(bidId);
            if (response?.success) {
                toast.success(
                    response.message ||
                        "Design proposal submitted successfully",
                );
                setShowSubmitModal(false);
                // Land on the "Submitted" tab (approved-by-contractor),
                // matching the execution-scope submit flow.
                navigate("/rfqs", { state: { tab: "awarded" } });
            } else {
                toast.error(
                    response?.message || "Failed to submit design proposal",
                );
            }
        } catch (error) {
            console.error("Error submitting design bid:", error);
            toast.error("Error submitting design proposal. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const projectImageUrl =
        proposalImages.find((img) => img && img.tags == null)?.image ||
        project?.project_image?.path ||
        project?.default_image?.[0]?.image;

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!data) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <Typography variant="h6">Bid details not found</Typography>
            </Box>
        );
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 0:
                return (
                    <DesignProposalResponse
                        projectId={project?.id || bidId}
                        proposalImages={proposalImages}
                        handleProposalImageUpload={handleProposalImageUpload}
                        handleDeleteProposalImage={handleDeleteProposalImage}
                        proposalDescription={proposalDescription}
                        setProposalDescription={setProposalDescription}
                        proposalAmount={proposalAmount}
                        setProposalAmount={setProposalAmount}
                        moodboards={data?.moodboards || []}
                        isDisabled={isDisabled}
                    />
                );
            case 1:
                return (
                    <DesignDeliverables
                        deliverables={deliverables}
                        setDeliverables={setDeliverables}
                        isDisabled={isDisabled}
                    />
                );
            case 2:
                return (
                    <DesignPayments
                        paymentRows={paymentRows}
                        proposalAmount={proposalAmount}
                    />
                );
            case 3:
                return (
                    <DesignReview
                        project={project}
                        proposalAmount={proposalAmount}
                        proposalDescription={proposalDescription}
                        deliverables={deliverables}
                        paymentRows={paymentRows}
                        moodboards={data?.moodboards || []}
                        projectImageUrl={projectImageUrl}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "row" }}>
            <Box
                sx={{
                    typography: "body1",
                    backgroundColor: "#fff",
                    height: "100%",
                    borderRadius: "6px",
                    fontFamily: "Inter Tight",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <SecurityPolicyModal
                    open={showSecurityModal}
                    onConfirm={handleSecurityModalClose}
                />

                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        padding: "3vh 2.5vw 1.5vh",
                        gap: "10px",
                        background:
                            "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
                        flexShrink: 0,
                    }}
                >
                    <LeftArrowIcon
                        onClick={() => navigate("/rfqs")}
                        style={{ cursor: "pointer" }}
                    />
                    <div
                        style={{
                            color: "#0D0D0E",
                            fontSize: "14px",
                            fontWeight: "500",
                        }}
                    >
                        {project?.name}
                    </div>
                </div>

                {/* Content + right drawer */}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "row",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            overflowY: "auto",
                            padding: "0 2.5vw",
                            "&::-webkit-scrollbar": {
                                width: 0,
                                background: "transparent",
                            },
                            scrollbarWidth: "none",
                        }}
                    >
                        {/* Pill tabs + Submit Bid at the right end */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "12px",
                                margin: "16px 0 24px",
                                flexShrink: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: "8px",
                                    overflowX: "auto",
                                    "&::-webkit-scrollbar": { height: 0 },
                                    scrollbarWidth: "none",
                                }}
                            >
                                {TABS.map((tab, index) => (
                                    <PillTab
                                        key={tab.id}
                                        index={index}
                                        label={tab.label}
                                        selected={activeTab === index}
                                        onClick={() => setActiveTab(index)}
                                    />
                                ))}
                            </Box>
                            {activeTab === 3 && !isDisabled && (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmitClick}
                                    sx={{
                                        backgroundColor: "#4D6BF6",
                                        color: "#fff",
                                        borderRadius: "10px",
                                        padding: "8px 20px",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        textTransform: "none",
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                        boxShadow: "none",
                                        fontFamily: "Inter Tight",
                                        "&:hover": {
                                            backgroundColor: "#3B5CE6",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    Submit Bid
                                </Button>
                            )}
                        </Box>

                        {renderTabContent()}

                        {/* Action bar — sticky at the bottom; the Review
                            tab keeps its Submit Bid next to the tabs, so
                            no bar there */}
                        {activeTab !== 3 && (
                        <Box
                            sx={{
                                position: "sticky",
                                bottom: 0,
                                zIndex: 2,
                                marginTop: "auto",
                                backgroundColor: "#fff",
                                borderTop: "1px solid #F3F4F6",
                                padding: "12px 0",
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    disabled={isSaving}
                                    sx={{
                                        backgroundColor: "#4D6BF6",
                                        color: "#fff",
                                        borderRadius: "8px",
                                        padding: "8px 24px",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        textTransform: "none",
                                        fontFamily: "Inter Tight",
                                        "&:hover": {
                                            backgroundColor: "#3B5CE6",
                                        },
                                        "&:disabled": {
                                            backgroundColor: "#E5E7EB",
                                            color: "#9CA3AF",
                                        },
                                    }}
                                >
                                    {isSaving ? (
                                        <>
                                            <CircularProgress
                                                size={16}
                                                sx={{ marginRight: "8px" }}
                                            />
                                            Saving...
                                        </>
                                    ) : (
                                        "Next"
                                    )}
                                </Button>
                        </Box>
                        )}
                    </Box>

                    {/* Right project drawer */}
                    {!isMobile && activeTab !== 3 && (
                        <ProjectDrawer
                            project={project}
                            proposalAmount={proposalAmount}
                            status={data?.proposal?.status}
                            isOpen={isDrawerOpen}
                            onToggle={() => setIsDrawerOpen((v) => !v)}
                        />
                    )}
                </Box>
            </Box>

            {/* Submit confirmation modal */}
            <ConfirmModel
                visible={showSubmitModal}
                loader={isSubmitting}
                handleClose={() => setShowSubmitModal(false)}
                confirmation={handleSubmitBid}
                title="Submit"
                message={
                    <>
                        <p>
                            Are you sure you want to submit this design
                            proposal?
                        </p>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                flexWrap: "wrap",
                                flex: 1,
                                marginTop: "1vh",
                            }}
                        >
                            {projectImageUrl && (
                                <Box
                                    component="img"
                                    src={projectImageUrl}
                                    alt="Project"
                                    sx={{
                                        width: 45,
                                        height: 45,
                                        borderRadius: "8px",
                                        border: "2px solid white",
                                        boxShadow:
                                            "0px 4px 2.67px 0px #00000026",
                                        marginRight: 1,
                                        flexShrink: 0,
                                        objectFit: "cover",
                                    }}
                                />
                            )}
                            <Typography
                                sx={{
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "normal",
                                    fontSize: "14px",
                                    fontWeight: "400",
                                }}
                            >
                                {project?.name || "Design Bid"}
                            </Typography>
                        </Box>
                    </>
                }
            />
        </div>
    );
}
